(() => {
  class ProductOptions {
    constructor(config = {}) {
      this.sectionId = config.sectionId || null;
      this.storeDomain = config.storeDomain || 'swissmadison.myshopify.com';
      this.storefrontAccessToken = config.storefrontAccessToken || '6ed228521db674dc1795e0d98c71c6ef';

      this.gids = []
        .concat(this.parseGids(config.gids1))
        .concat(this.parseGids(config.gids2))
        .concat(this.parseGids(config.gids3));

      this.gids = Array.from(new Set(this.gids));

      this.relatedProducts = [];

      this.shopifyQuery = `query getProducts($ids: [ID!]!, $identifiers: [HasMetafieldsIdentifier!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            title
            handle
            featuredImage {
              url
            }
            metafields(identifiers: $identifiers) {
              key
              namespace
              value
              type
            }
          }
        }
      }`;
    }

    init() {
      if (!this.gids.length) return;

      const identifiers = this.getIdentifiers();
      if (!identifiers.length) return;

      this.fetchRelatedProducts(identifiers)
        .then(() => {
          this.bindCustomOptionChange();
        })
        .catch((error) => {
          console.error('[ProductOptions] Shopify fetch error:', error);
        });
    }

    getScope() {
      if (!this.sectionId) return document;
      return document.getElementById(this.sectionId) || document;
    }

    query(selector) {
      return this.getScope().querySelector(selector);
    }

    queryAll(selector) {
      return Array.from(this.getScope().querySelectorAll(selector));
    }

    normalizeGid(item) {
      if (!item) return null;

      if (typeof item === 'string') {
        const trimmed = item.trim();
        return trimmed || null;
      }

      if (typeof item === 'object') {
        if (Array.isArray(item.value)) {
          return null;
        }

        return (
          item.admin_graphql_api_id ||
          item.id ||
          item.gid ||
          (typeof item.value === 'string' ? item.value : null)
        );
      }

      return null;
    }

    parseGids(value) {
      if (!value) return [];

      if (Array.isArray(value)) {
        return value.map((item) => this.normalizeGid(item)).filter(Boolean);
      }

      if (typeof value === 'object') {
        if (Array.isArray(value.value)) {
          return value.value.map((item) => this.normalizeGid(item)).filter(Boolean);
        }

        const gid = this.normalizeGid(value);
        return gid ? [gid] : [];
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];

        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            return this.parseGids(parsed);
          } catch (error) {
            console.warn('[ProductOptions] Failed parsing gid JSON string', error);
          }
        }

        return this.parseGids(this.normalizeGid(trimmed));
      }

      return [];
    }

    norm(value) {
      return String(value ?? '').toLowerCase().trim();
    }

    includesVal(mfVal, target) {
      const t = this.norm(target);
      if (!t) return false;

      if (Array.isArray(mfVal)) {
        return mfVal.some((v) => this.norm(v) === t);
      }

      const s = this.norm(mfVal);
      if (!s) return false;

      if (/[,|;]/.test(s)) {
        const parts = s.split(/[,|;]\s*/).map((part) => this.norm(part));
        return parts.some((part) => part === t);
      }

      return s === t;
    }

    parseMetafieldValue(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;

      if (typeof value === 'string') {
        const trimmed = value.trim();

        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed) ? parsed : [parsed];
          } catch (error) {
            console.warn('[ProductOptions] Failed to parse metafield JSON value', {
              raw: value,
              error,
            });
            return [value];
          }
        }

        return [value];
      }

      return [value];
    }

    getIdentifiers() {
      return this.queryAll('.product__options .custom-option').map((el) => ({
        namespace: el.getAttribute('namespace'),
        key: el.getAttribute('key'),
      }));
    }

    async fetchRelatedProducts(identifiers) {
      const response = await fetch(`https://${this.storeDomain}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken,
        },
        body: JSON.stringify({
          query: this.shopifyQuery,
          variables: { ids: this.gids, identifiers },
        }),
      });

      const result = await response.json();
      if (!result.data?.nodes) {
        throw new Error('No nodes returned');
      }

      this.relatedProducts = (result.data.nodes || [])
        .filter(Boolean)
        .map((item) => {
          const metafields = (item.metafields || []).map((metafield) => ({
            key: metafield?.key,
            value: this.parseMetafieldValue(metafield?.value),
          }));

          return {
            id: item.id,
            title: item.title,
            handle: item.handle,
            image: item.featuredImage?.url || '',
            metafields: metafields.filter((m) => m.key && m.value?.length),
          };
        });
    }

    bindCustomOptionChange() {
      this.disableUnavailableOptions(null, null, true);

      this.queryAll('.custom-option-values label').forEach((label) => {
        const input = label.querySelector('input');
        if (!input) return;

        if (label.dataset.relatedHoverBound === 'true') return;
        label.dataset.relatedHoverBound = 'true';

        label.addEventListener('mouseenter', () => {
          const filteredItem = this.disableUnavailableOptions(input.name, input.value, false);

          if (filteredItem) {
            this.preloadPage(`/products/${filteredItem.handle}`);
          } else {
            console.warn('[ProductOptions] No related product found to preload');
          }
        });
      });

      this.queryAll('.custom-option input').forEach((input) => {
        if (input.dataset.relatedChangeBound === 'true') return;
        input.dataset.relatedChangeBound = 'true';

        input.addEventListener('change', (event) => {
          const filteredItem = this.disableUnavailableOptions(input.name, input.value, true);

          if (filteredItem) {
            const spinner = this.query('.product__options .loading__spinner');
            if (spinner) {
              spinner.classList.remove('hidden');
            }

            const mainImage = this.query('.product__media-item.is-active .product__media img');

            if (mainImage && filteredItem.image) {
              const originalSrc = mainImage.getAttribute('data-src') || mainImage.getAttribute('src');
              const originalSrcset = mainImage.getAttribute('data-srcset');

              mainImage.setAttribute('src', filteredItem.image);
              mainImage.setAttribute('data-src', originalSrc || '');

              if (originalSrcset) {
                mainImage.setAttribute('data-srcset', originalSrcset);
              }

              mainImage.removeAttribute('srcset');
            }

            const targetUrl = `/products/${filteredItem.handle}`;

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.optionChange, {
                data: {
                  event,
                  target: event.target,
                  url: targetUrl,
                  callback: () => {
                    this.bindCustomOptionChange();
                    if (typeof setupSwatches === 'function') {
                      setupSwatches();
                    }
                  },
                },
              });
            }
          } else {
            console.warn('[ProductOptions] No valid related product found. Marking as unavailable.');

            const submitBtn = this.query('.product-form__submit');
            const tooltip = input.closest('.custom-option')?.querySelector('.custom-option-tooltip');

            if (submitBtn) {
              submitBtn.innerHTML = 'Unavailable';
              submitBtn.disabled = true;
            }

            if (tooltip) {
              tooltip.innerHTML = '<p class="unavailable">Unavailable</p>';
            }
          }
        });
      });
    }

    disableUnavailableOptions(changedKey, newValue, update) {
      const selectedOptions = {};

      this.queryAll('.custom-option-values input:checked').forEach((input) => {
        selectedOptions[input.name] = input.value;
      });

      if (changedKey && newValue) {
        selectedOptions[changedKey] = newValue;
      }

      const filteredItem = this.findRelatedProduct(selectedOptions, { changedKey, newValue });

      if (update) {
        this.queryAll('.custom-option-values input').forEach((input) => {
          const newFilters = { ...selectedOptions, [input.name]: input.value };

          const match = this.relatedProducts.find((item) => {
            if (!item?.metafields) return false;

            return Object.entries(newFilters).every(([key, value]) =>
              item.metafields.some((mf) => mf.key === key && this.includesVal(mf.value, value))
            );
          });

          if (match) {
            input.classList.remove('disabled');
          } else {
            input.classList.add('disabled');
          }
        });
      }

      return filteredItem;
    }

    findRelatedProduct(selectedOptions, opts = {}) {
      const { changedKey = null, newValue = null } = opts;

      const exactMatch = this.relatedProducts.find((item) => {
        if (!item?.metafields) return false;

        return Object.entries(selectedOptions).every(([key, value]) =>
          item.metafields.some((mf) => mf.key === key && this.includesVal(mf.value, value))
        );
      });

      if (exactMatch) {
        return exactMatch;
      }

      let best = { score: -1, item: null };

      for (const item of this.relatedProducts) {
        const mfs = item?.metafields || [];

        if (changedKey && newValue != null) {
          const changedOk = mfs.some(
            (mf) => mf.key === changedKey && this.includesVal(mf.value, newValue)
          );

          if (!changedOk) continue;
        }

        let score = 0;

        for (const [key, val] of Object.entries(selectedOptions)) {
          if (key === changedKey) continue;

          const ok = mfs.some((mf) => mf.key === key && this.includesVal(mf.value, val));
          if (ok) score += 1;
        }

        if (score > best.score) {
          best = { score, item };
        }
      }

      return best.item || null;
    }

    preloadPage(url) {
      const existing = document.querySelector(`link[rel="prefetch"][href="${url}"]`);
      if (existing) return;

      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    }
  }

  window.ProductOptions = ProductOptions;
})();
